import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Toaster } from "sonner";

import { LanguageProvider } from "@/lib/i18n";
import { usePrefersReducedMotion } from "@/lib/useReducedMotion";
import { Preloader } from "@/components/Preloader";
import { CustomCursor } from "@/components/CustomCursor";
import { ScrollProgress } from "@/components/ScrollProgress";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Navbar } from "@/components/Navbar";

import { Hero } from "@/sections/Hero";
import { Squeegee } from "@/sections/Squeegee";
import { SelfHealing } from "@/sections/SelfHealing";
import { BeforeAfter } from "@/sections/BeforeAfter";
import { WrapConfigurator } from "@/sections/WrapConfigurator";
import { WindowTint } from "@/sections/WindowTint";
import { Services } from "@/sections/Services";
import { Timeline } from "@/sections/Timeline";
import { Process } from "@/sections/Process";
import { Gallery } from "@/sections/Gallery";
import { Warranty } from "@/sections/Warranty";
import { Faq } from "@/sections/Faq";
import { Contact } from "@/sections/Contact";
import { Footer } from "@/sections/Footer";
import Admin from "@/pages/Admin";

gsap.registerPlugin(ScrollTrigger);

function SmoothScroll({ children }) {
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    if (reduced) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true, wheelMultiplier: 1 });
    lenis.on("scroll", ScrollTrigger.update);
    const raf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    const onClick = (e) => {
      const a = e.target.closest?.('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (id === "#" || id === "#top") {
        e.preventDefault();
        lenis.scrollTo(0);
        return;
      }
      const el = document.querySelector(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -60 });
      }
    };
    document.addEventListener("click", onClick);

    return () => {
      document.removeEventListener("click", onClick);
      gsap.ticker.remove(raf);
      lenis.destroy();
    };
  }, [reduced]);
  return children;
}

function MainSite() {
  useEffect(() => {
    const id = setTimeout(() => ScrollTrigger.refresh(), 800);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="App bg-[#08090B]">
      <Preloader />
      <CustomCursor />
      <ScrollProgress />
      <Navbar />
      <WhatsAppButton />
      <SmoothScroll>
        <main>
          <Hero />
          <Squeegee />
          <SelfHealing />
          <BeforeAfter />
          <WrapConfigurator />
          <WindowTint />
          <Services />
          <Timeline />
          <Process />
          <Gallery />
          <Warranty />
          <Faq />
          <Contact />
          <Footer />
        </main>
      </SmoothScroll>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Toaster position="top-center" theme="dark" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainSite />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
